import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';

const BrandContext = createContext();

const initialState = {
  brands: [],
  selectedBrand: null,
  loading: true,
  error: null,
  analytics: null
};

const brandReducer = (state, action) => {
  switch (action.type) {
    case 'BRANDS_LOADING':
      return {
        ...state,
        loading: true,
        error: null
      };
    case 'BRANDS_SUCCESS':
      return {
        ...state,
        brands: action.payload,
        loading: false,
        error: null
      };
    case 'BRANDS_FAILURE':
      return {
        ...state,
        brands: [],
        loading: false,
        error: action.payload
      };
    case 'SELECT_BRAND':
      return {
        ...state,
        selectedBrand: action.payload
      };
    case 'ADD_BRAND':
      return {
        ...state,
        brands: [...state.brands, action.payload]
      };
    case 'UPDATE_BRAND':
      return {
        ...state,
        brands: state.brands.map(brand =>
          brand.brand_id === action.payload.brand_id ? action.payload : brand
        ),
        selectedBrand: state.selectedBrand?.brand_id === action.payload.brand_id 
          ? action.payload 
          : state.selectedBrand
      };
    case 'DELETE_BRAND':
      return {
        ...state,
        brands: state.brands.filter(brand => brand.brand_id !== action.payload),
        selectedBrand: state.selectedBrand?.brand_id === action.payload 
          ? null 
          : state.selectedBrand
      };
    case 'SET_ANALYTICS':
      return {
        ...state,
        analytics: action.payload
      };
    default:
      return state;
  }
};

export const BrandProvider = ({ children }) => {
  const [state, dispatch] = useReducer(brandReducer, initialState);
  const { token } = useAuth();

  const loadBrands = useCallback(async () => {
    try {
      dispatch({ type: 'BRANDS_LOADING' });
      const response = await api.get('/brands');
      dispatch({ type: 'BRANDS_SUCCESS', payload: response.data.brands });
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load brands';
      dispatch({ type: 'BRANDS_FAILURE', payload: message });
      toast.error(message);
    }
  }, []);

  // Load brands whenever auth token becomes available/changes
  useEffect(() => {
    if (!token) {
      dispatch({ type: 'BRANDS_FAILURE', payload: null });
      return;
    }
    loadBrands();
  }, [token, loadBrands]);

  const getBrand = async (brandId) => {
    try {
      const response = await api.get(`/brands/${brandId}`);
      const { brand, teamMembers, socialAccounts } = response.data;
      const enriched = { ...brand, teamMembers, socialAccounts };
      dispatch({ type: 'UPDATE_BRAND', payload: enriched });
      dispatch({ type: 'SELECT_BRAND', payload: enriched });
      return { success: true, brand: enriched };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load brand';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const selectBrand = (brand) => {
    dispatch({ type: 'SELECT_BRAND', payload: brand });
    if (brand) {
      localStorage.setItem('selectedBrand', JSON.stringify(brand));
    } else {
      localStorage.removeItem('selectedBrand');
    }
  };

  const switchBrand = async (brandId) => {
    try {
      const brand = state.brands.find(b => b.brand_id === brandId);
      if (brand) {
        // Load detailed brand info including team members and social accounts
        const result = await getBrand(brandId);
        if (result.success) {
          toast.success(`Switched to ${brand.brand_name}`);
          return { success: true };
        }
      }
      return { success: false, error: 'Brand not found' };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to switch brand';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const createBrand = async (brandData) => {
    try {
      const response = await api.post('/brands', brandData);
      const brandId = response.data?.brandId;
      // Reload list to reflect the new brand
      await loadBrands();
      toast.success('Brand created successfully');
      return { success: true, brandId };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create brand';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateBrand = async (brandId, brandData) => {
    try {
      await api.put(`/brands/${brandId}`, brandData);
      // Refresh brand detail and list
      await getBrand(brandId);
      await loadBrands();
      toast.success('Brand updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update brand';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const deleteBrand = async (brandId) => {
    try {
      await api.delete(`/brands/${brandId}`);
      
      dispatch({ type: 'DELETE_BRAND', payload: brandId });
      toast.success('Brand deleted successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete brand';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const addTeamMember = async (brandId, email, role) => {
    try {
      await api.post(`/brands/${brandId}/team`, { email, role });
      // Refresh members list
      await getBrand(brandId);
      toast.success('Team member added successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to add team member';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateTeamMemberRole = async (brandId, userId, role) => {
    try {
      await api.put(`/brands/${brandId}/team/${userId}`, { role });
      await getBrand(brandId);
      toast.success('Team member role updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update team member role';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const removeTeamMember = async (brandId, userId) => {
    try {
      await api.delete(`/brands/${brandId}/team/${userId}`);
      await getBrand(brandId);
      toast.success('Team member removed successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to remove team member';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Enhanced Team Management Functions
  const inviteTeamMember = async (brandId, inviteData) => {
    try {
      const response = await api.post(`/team/brand/${brandId}/invite`, inviteData);
      await getBrand(brandId);
      toast.success('Team member invitation sent successfully');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send invitation';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateTeamMember = async (userId, memberData) => {
    if (!state.selectedBrand) {
      const message = 'No brand selected';
      toast.error(message);
      return { success: false, error: message };
    }
    try {
      await api.put(`/team/user/${userId}`, { ...memberData, brandId: state.selectedBrand.brand_id });
      await getBrand(state.selectedBrand.brand_id);
      toast.success('Team member updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update team member';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const getTeamMembers = async (brandId) => {
    try {
      const response = await api.get(`/team/brand/${brandId}`);
      return { success: true, teamMembers: response.data.teamMembers };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load team members';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const getPendingInvitations = async () => {
    try {
      const response = await api.get('/team/invitations/pending');
      return { success: true, invitations: response.data.invitations || [] };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load invitations';
      return { success: false, error: message, invitations: [] };
    }
  };

  const acceptInvitation = async (invitationId) => {
    try {
      await api.post(`/team/invitation/${invitationId}/accept`);
      toast.success('Invitation accepted');
      await loadBrands();
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to accept invitation';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const rejectInvitation = async (invitationId) => {
    try {
      await api.post(`/team/invitation/${invitationId}/reject`);
      toast.success('Invitation rejected');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reject invitation';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const getNotifications = async (brandId) => {
    try {
      const response = await api.get(`/notifications/brand/${brandId}`);
      return { success: true, notifications: response.data.notifications };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load notifications';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      toast.success('Notification marked as read');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update notification';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      toast.success('Notification deleted');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete notification';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const markAllNotificationsAsRead = async (brandId) => {
    try {
      await api.put(`/notifications/brand/${brandId}/read-all`);
      toast.success('All notifications marked as read');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update notifications';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // AI content generation — every call is persisted server-side to
  // ai_suggestions automatically, so there's no separate "create" endpoint;
  // "using" a suggestion is a distinct mark-as-used call.
  const generateAIContent = async ({ brandId, prompt, contentType = 'caption', tone = 'professional', platform = 'all', maxLength = 200 }) => {
    try {
      const response = await api.post('/ai/generate', { brandId, prompt, contentType, tone, platform, maxLength });
      return { success: true, ...response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to generate content';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const getAISuggestions = async (brandId) => {
    try {
      const response = await api.get(`/ai/brand/${brandId}/suggestions`);
      return { success: true, suggestions: response.data.suggestions || [] };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load suggestions';
      return { success: false, error: message, suggestions: [] };
    }
  };

  const markAISuggestionUsed = async (suggestionId) => {
    try {
      await api.put(`/ai/suggestions/${suggestionId}/use`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to save suggestion';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const deleteAISuggestion = async (suggestionId) => {
    try {
      await api.delete(`/ai/suggestions/${suggestionId}`);
      toast.success('Suggestion deleted');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete suggestion';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Report generation is stateless on the backend today — there is no
  // persisted report history to list, only two "generate & return" endpoints.
  // CSV downloads for real; other formats are surfaced honestly (see Reports.js).
  const generateReport = async (brandId, { category = 'analytics', startDate, endDate, format, platforms = [] } = {}) => {
    const endpoint = category === 'content' ? 'content' : 'analytics';
    try {
      if (format === 'csv') {
        const response = await api.post(
          `/reports/brand/${brandId}/${endpoint}`,
          { startDate, endDate, format, platforms },
          { responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${endpoint}-report-${brandId}-${startDate}-to-${endDate}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded');
        return { success: true };
      }

      const response = await api.post(`/reports/brand/${brandId}/${endpoint}`, { startDate, endDate, format, platforms });
      toast.success('Report generated');
      return { success: true, data: response.data?.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to generate report';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const computeDateRange = (rangeKey) => {
    const end = new Date();
    const start = new Date();
    const map = {
      '7d': 7,
      '30d': 30,
      '90d': 90,
      '1y': 365
    };
    const days = map[rangeKey] || 7;
    start.setDate(start.getDate() - days);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { startDate: fmt(start), endDate: fmt(end) };
  };

  const createPost = async (brandId, postData) => {
    try {
      const response = await api.post(`/posts/brands/${brandId}`, postData);
      toast.success('Post created successfully');
      return { success: true, data: response.data };
    } catch (error) {
      // express-validator failures come back as {errors: [{msg, path}]},
      // not {message} — surface the real reason instead of a generic one.
      const message = error.response?.data?.message
        || error.response?.data?.errors?.map((e) => `${e.path || e.param}: ${e.msg}`).join('; ')
        || 'Failed to create post';

      // A 502 here means SOME platforms published and the rest failed
      // (see routes/posts.js) — every other platform already went out, so
      // this reads as a success with a note, not a flat failure, matching
      // how Buffer/Hootsuite handle a multi-platform post where only one
      // target rejects it: the rest post anyway, the one that failed shows
      // up for a retry (SocialFlow's Posts > Failed tab).
      const data = error.response?.data;
      // succeededPlatforms is always an array here, even when empty, so check
      // its length — [] is truthy and would otherwise be read as a partial
      // success and reset the Composer even though nothing actually posted.
      if (error.response?.status === 502 && data?.succeededPlatforms?.length > 0) {
        toast.success(`Posted to ${data.succeededPlatforms.join(', ')}`);
        toast.error(`Failed: ${message}`);
        return { success: true, partial: true, data, failures: data.failures };
      }

      toast.error(message);
      return { success: false, error: message, failures: data?.failures };
    }
  };

  const updatePost = async (postId, postData) => {
    try {
      await api.put(`/posts/${postId}`, postData);
      toast.success('Post updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update post';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const deletePost = async (postId) => {
    try {
      await api.delete(`/posts/${postId}`);
      toast.success('Post deleted successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete post';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const publishPost = async (postId) => {
    try {
      await api.post(`/posts/${postId}/publish`);
      toast.success('Post published successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to publish post';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const loadPosts = async (brandId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const response = await api.get(`/posts/brand/${brandId}?${params.toString()}`);
      return { 
        success: true, 
        posts: response.data.posts || [],
        pagination: response.data.pagination 
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load posts';
      return { success: false, error: message, posts: [] };
    }
  };

  const loadCalendarPosts = async (brandId, startDate, endDate) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api.get(`/posts/brand/${brandId}/calendar?${params.toString()}`);
      return {
        success: true,
        posts: response.data.posts || []
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load calendar posts';
      return { success: false, error: message, posts: [] };
    }
  };

  // Media Library Functions
  const loadMedia = async (brandId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.type) params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);
      if (filters.tags) params.append('tags', filters.tags);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const response = await api.get(`/media/brand/${brandId}?${params.toString()}`);
      return {
        success: true,
        media: response.data.media || [],
        pagination: response.data.pagination
      };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to load media';
      return { success: false, error: message, media: [] };
    }
  };

  const uploadMedia = async (brandId, file, metadata = {}) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (metadata.altText) formData.append('altText', metadata.altText);
      if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));

      const response = await api.post(`/media/brand/${brandId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          return percentCompleted;
        }
      });

      toast.success('Media uploaded successfully');
      return { success: true, media: response.data.file };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to upload media';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateMedia = async (mediaId, metadata) => {
    try {
      await api.put(`/media/${mediaId}`, metadata);
      toast.success('Media updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update media';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const deleteMedia = async (mediaId) => {
    try {
      await api.delete(`/media/${mediaId}`);
      toast.success('Media deleted successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete media';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Social Account Management Functions
  const connectAccount = async (brandId, platform) => {
    try {
      const response = await api.get(`/social-accounts/oauth/${platform}`);
      return { success: true, authUrl: response.data.oauthUrl };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to get OAuth URL';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const disconnectAccount = async (accountId) => {
    try {
      await api.delete(`/social-accounts/${accountId}`);
      // Refresh brand data to update social accounts
      if (state.selectedBrand) {
        await getBrand(state.selectedBrand.brand_id);
      }
      toast.success('Account disconnected successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to disconnect account';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const refreshAccount = async (accountId) => {
    try {
      await api.post(`/social-accounts/${accountId}/sync`);
      // Refresh brand data to update social accounts
      if (state.selectedBrand) {
        await getBrand(state.selectedBrand.brand_id);
      }
      toast.success('Account refreshed successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to refresh account';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const toggleAccountActive = async (accountId) => {
    try {
      const response = await api.put(`/social-accounts/${accountId}/toggle-active`);
      if (state.selectedBrand) {
        await getBrand(state.selectedBrand.brand_id);
      }
      toast.success(response.data?.message || 'Account status updated');
      return { success: true, isActive: response.data?.is_active };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update account status';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const handleOAuthCallback = async (platform, code, brandId, oauthState) => {
    try {
      const response = await api.post(`/social-accounts/oauth/${platform}/callback`, {
        brandId,
        code,
        state: oauthState
      });
      // Refresh brand data to update social accounts
      if (state.selectedBrand) {
        await getBrand(state.selectedBrand.brand_id);
      }
      toast.success('Account connected successfully');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to connect account';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Bluesky isn't OAuth — it's a direct handle + app-password exchange (see
  // POST /social-accounts/bluesky/connect), so it gets its own connect
  // function instead of going through connectAccount/handleOAuthCallback.
  const connectBlueskyAccount = async (brandId, handle, appPassword) => {
    try {
      const response = await api.post('/social-accounts/bluesky/connect', { brandId, handle, appPassword });
      if (state.selectedBrand) {
        await getBrand(state.selectedBrand.brand_id);
      }
      toast.success('Bluesky account connected successfully');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to connect Bluesky account';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // WordPress isn't OAuth either — a site URL + username + application
  // password exchange (see POST /social-accounts/wordpress/connect).
  const connectWordPressAccount = async (brandId, siteUrl, username, appPassword) => {
    try {
      const response = await api.post('/social-accounts/wordpress/connect', { brandId, siteUrl, username, appPassword });
      if (state.selectedBrand) {
        await getBrand(state.selectedBrand.brand_id);
      }
      toast.success('WordPress site connected successfully');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to connect WordPress site';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Quora is assisted posting — connecting just records the profile or
  // Space link (see server/routes/socialAccounts.js /quora/connect).
  const connectQuoraAccount = async (brandId, url, name) => {
    try {
      const response = await api.post('/social-accounts/quora/connect', { brandId, url, name });
      if (state.selectedBrand) {
        await getBrand(state.selectedBrand.brand_id);
      }
      toast.success('Quora connected');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to connect Quora';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // After posting on Quora by hand, record the live link.
  const markPostedOnQuora = async (postId, url) => {
    try {
      const response = await api.post(`/posts/${postId}/mark-posted`, { url });
      toast.success('Marked as posted on Quora');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message
        || error.response?.data?.errors?.map((e) => e.msg).join('; ')
        || 'Could not mark as posted';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const loadWordPressCategories = async (accountId) => {
    try {
      const response = await api.get(`/social-accounts/${accountId}/wordpress-categories`);
      return { success: true, categories: response.data.categories || [] };
    } catch (error) {
      const message = error.response?.data?.message || 'Could not load categories';
      return { success: false, error: message, categories: [] };
    }
  };

  const publishBlogPost = async (accountId, { title, contentHtml, categoryId, status }) => {
    try {
      const response = await api.post(`/posts/wordpress/${accountId}`, { title, contentHtml, categoryId, status });
      toast.success(status === 'draft' ? 'Draft saved on WordPress' : 'Published to WordPress');
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to publish to WordPress';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Track ongoing requests to prevent duplicates and add debouncing
  const analyticsRequestRef = useRef({});
  const debounceTimerRef = useRef({});
  
  const fetchAnalytics = useCallback(async (brandId, rangeKey = '7d', platform = 'all') => {
    // Create a unique key for this request
    const requestKey = `${brandId}-${rangeKey}-${platform}`;
    
    // Clear any existing debounce timer for this request
    if (debounceTimerRef.current[requestKey]) {
      clearTimeout(debounceTimerRef.current[requestKey]);
      delete debounceTimerRef.current[requestKey];
    }
    
    // If a request is already in progress, return the existing promise
    if (analyticsRequestRef.current[requestKey]) {
      return analyticsRequestRef.current[requestKey];
    }
    
    // Create the promise and store it
    const requestPromise = (async () => {
      try {
        const { startDate, endDate } = computeDateRange(rangeKey);
        const params = {
          startDate,
          endDate,
          ...(platform && platform !== 'all' ? { platform } : {})
        };
        
        const [summaryRes, topPostsRes] = await Promise.all([
          api.get(`/analytics/brand/${brandId}/summary`, { params }),
          api.get(`/analytics/brand/${brandId}/top-posts`, { params: { startDate, endDate, ...(platform && platform !== 'all' ? { platform } : {}), limit: 5 } })
        ]);

        const { summary, byPlatform, dailyData } = summaryRes.data || {};
        const topPosts = topPostsRes.data?.posts || [];

        const totalEngagement = (summary?.total_likes || 0) + (summary?.total_comments || 0) + (summary?.total_shares || 0);
        const reachData = (dailyData || []).map(d => ({
          date: d.date,
          reach: d.reach || 0,
          engagement: (d.likes || 0) + (d.comments || 0) + (d.shares || 0)
        }));
        const platformData = (byPlatform || []).map(p => ({ name: p.platform, value: p.followers || 0 }));
        const platformEngagement = (byPlatform || []).reduce((acc, p) => {
          acc[p.platform] = p.engagement_rate || 0;
          return acc;
        }, {});

        const mappedTopPosts = topPosts.map(p => ({
          post_id: p.post_id,
          content: p.content_text || '',
          likes: p.likes || 0,
          comments: p.comments || 0,
          shares: p.shares || 0,
          platform: p.platform || 'all'
        }));

        const analytics = {
          totalReach: summary?.total_reach || 0,
          totalEngagement,
          totalFollowers: summary?.total_followers || 0,
          totalPosts: summary?.total_posts || 0,
          engagementRate: summary?.avg_engagement_rate || 0,
          reachData,
          platformData,
          topPosts: mappedTopPosts,
          platformEngagement
        };

        dispatch({ type: 'SET_ANALYTICS', payload: analytics });
        
        // Clear the request from ref after completion
        delete analyticsRequestRef.current[requestKey];
        
        return { success: true };
      } catch (error) {
        // Clear the request from ref on error
        delete analyticsRequestRef.current[requestKey];
        
        // Don't show error toast for rate limiting (429) - just fail silently
        if (error.response?.status === 429) {
          console.warn('Analytics request rate limited. Please wait before retrying.');
          dispatch({ type: 'SET_ANALYTICS', payload: null });
          return { success: false, error: 'Rate limited. Please wait before retrying.' };
        }
        
        const message = error.response?.data?.message || 'Failed to load analytics';
        // Only show toast for non-rate-limit errors
        if (error.response?.status !== 429) {
          toast.error(message);
        }
        dispatch({ type: 'SET_ANALYTICS', payload: null });
        return { success: false, error: message };
      }
    })();
    
    // Store the promise
    analyticsRequestRef.current[requestKey] = requestPromise;
    
    return requestPromise;
  }, []);

  // Load selected brand from localStorage on mount
  useEffect(() => {
    const savedBrand = localStorage.getItem('selectedBrand');
    if (savedBrand && state.brands.length > 0) {
      try {
        const brand = JSON.parse(savedBrand);
        const currentBrand = state.brands.find(b => b.brand_id === brand.brand_id);
        if (currentBrand) {
          selectBrand(currentBrand);
        }
      } catch (error) {
        localStorage.removeItem('selectedBrand');
      }
    }
  }, [state.brands]);

  const value = {
    ...state,
    loadBrands,
    getBrand,
    selectBrand,
    switchBrand,
    createBrand,
    updateBrand,
    deleteBrand,
    addTeamMember,
    updateTeamMemberRole,
    removeTeamMember,
    inviteTeamMember,
    updateTeamMember,
    getTeamMembers,
    getPendingInvitations,
    acceptInvitation,
    rejectInvitation,
    getNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
    generateAIContent,
    getAISuggestions,
    markAISuggestionUsed,
    deleteAISuggestion,
    createPost,
    updatePost,
    deletePost,
    publishPost,
    loadPosts,
    loadCalendarPosts,
    loadMedia,
    uploadMedia,
    updateMedia,
    deleteMedia,
    connectAccount,
    connectBlueskyAccount,
    connectWordPressAccount,
    connectQuoraAccount,
    markPostedOnQuora,
    loadWordPressCategories,
    publishBlogPost,
    disconnectAccount,
    refreshAccount,
    toggleAccountActive,
    handleOAuthCallback,
    fetchAnalytics,
    generateReport
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
};





